/**
 * Face landmarks, in the real app — a phone-testable version of
 * `exploring/face-landmarks/index.html` (see that file's README for what was
 * already verified: real detection, real coordinates, dots actually painted,
 * not just computed). Reachable at `/experiments/face-landmarks` for exactly
 * the accounts in `lib/testers.ts`; a 🧪 in `AppHeader` links here for them.
 *
 * Why this needed to move out of `exploring/`: testing a webcam demo or a
 * `nip.io`-tunneled dev server on a real phone is real friction (see
 * README.md's phone-testing section) compared to just opening the deployed
 * site. This is the same MediaPipe pipeline, same pinned CDN version, now
 * reachable at the one URL that's already open on the phone in your pocket.
 *
 * Still fully client-side: the photo is never sent to this app's backend —
 * only to MediaPipe's own CDN for the WASM runtime and model file, once,
 * cached after that. See `lib/testers.ts`'s doc comment for why gating this
 * client-side only is a considered choice here, not a shortcut.
 *
 * Deliberately not internationalized (see the disabled lint rule below) —
 * this is a temporary internal diagnostic for one account, not user-facing
 * product copy, and a chunk of what it renders (MediaPipe's own blendshape
 * category names like "eyeLookOutLeft") is English API output that
 * shouldn't be translated even if this screen stuck around.
 */
/* eslint-disable i18next/no-literal-string */
import type { Application as PixiApplication, MeshSimple as PixiMeshSimple } from 'pixi.js';
import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router';
import { useSession } from '../auth-client.js';
import {
  colorForLandmark,
  FACE_REGIONS,
  OTHER_MESH_COLOR,
  OTHER_MESH_LABEL,
} from '../lib/faceLandmarkRegions.js';
import { FACEMESH_TESSELATION_FLAT } from '../lib/faceMeshTesselation.js';
import { isTester } from '../lib/testers.js';
import ExpressionLab from '../components/ExpressionLab.js';
import BrowserExpressionProbe from '../components/BrowserExpressionProbe.js';
import BrowserPhotoLab from '../components/BrowserPhotoLab.js';
import styles from './FaceLandmarksExperimentScreen.module.css';

// Pinned, matching exploring/face-landmarks/index.html exactly — see that
// file's own comment for why "@latest" is the wrong choice here.
const TASKS_VISION_VERSION = '1.0.1';
const CDN_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${TASKS_VISION_VERSION}`;
const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task';

// A handful of commonly-cited indices, worth eyeballing against the photo —
// verify against MediaPipe's own landmark-index map before trusting any
// index you haven't checked yourself (FACE_FILTERS_ROADMAP.md, Phase 1).
const NAMED_LANDMARKS: Record<string, number> = {
  'nose tip': 1,
  chin: 152,
  'left eye outer': 33,
  'right eye outer': 263,
  'mouth left': 61,
  'mouth right': 291,
};

// Sculpt-only region categories — 5 of the 6 named FACE_REGIONS (Lips,
// Eyes, Eyebrows, Nose, Face outline — NOT Iris, see below) plus 3 new ones
// splitting the ~330 previously-uncategorized "other" points into
// Forehead/Cheeks/Jaw. Unlike the named regions (sourced from MediaPipe's
// own FACEMESH_* connector definitions), there's no official "cheek"
// region in MediaPipe's topology — this split is a geometric heuristic
// computed fresh from each photo's OWN detected landmarks (not a fixed
// guessed index list), in buildSculptCategoryIndices() below: above the
// eyebrow-row average height is forehead, below the mouth-row average is
// jaw, otherwise cheeks.
//
// Iris excluded on purpose, not an oversight: its 10 landmarks (indices
// 468-477) sit outside the 0-467 base mesh entirely — they were never part
// of `vertices`/`currentVerticesRef` (built with vertexCount = 468, see
// renderMesh() below) or the tesselation this whole warp system operates
// on. Including it here would silently read undefined array slots.
const SCULPT_CATEGORY_COLORS: Record<string, string> = {
  Lips: '#ff1744',
  Eyes: '#ffea00',
  Eyebrows: '#ff9100',
  Nose: '#00e676',
  'Face outline': '#00b0ff',
  Forehead: '#9e9e9e',
  Cheeks: '#ff80ab',
  Jaw: '#795548',
};

// The shape MediaPipe's JS API actually returns — confirmed live against a
// real photo while building exploring/face-landmarks/, not assumed from the
// docs, whose own written example uses Python's snake_case names instead.
interface Landmark {
  x: number;
  y: number;
  z: number;
}
interface BlendshapeCategory {
  categoryName: string;
  score: number;
}
interface FaceLandmarkerResult {
  faceLandmarks: Landmark[][];
  faceBlendshapes?: { categories: BlendshapeCategory[] }[];
}
interface FaceLandmarker {
  detect(image: HTMLImageElement | HTMLCanvasElement): FaceLandmarkerResult;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; landmarker: FaceLandmarker }
  | { kind: 'error'; message: string };

export default function FaceLandmarksExperimentScreen() {
  const { data: session, isPending } = useSession();

  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [status, setStatus] = useState('Loading MediaPipe…');
  const [dragging, setDragging] = useState(false);
  const [lastResult, setLastResult] = useState<FaceLandmarkerResult | null>(null);
  const [meshStatus, setMeshStatus] = useState<string | null>(null);
  const [meshBusy, setMeshBusy] = useState(false);
  const [meshReady, setMeshReady] = useState(false);
  const [smileStatus, setSmileStatus] = useState<string | null>(null);
  const [smileBusy, setSmileBusy] = useState(false);
  const [sadStatus, setSadStatus] = useState<string | null>(null);
  const [sadBusy, setSadBusy] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const meshCanvasRef = useRef<HTMLCanvasElement>(null);
  const smileCanvasRef = useRef<HTMLCanvasElement>(null);
  const sadCanvasRef = useRef<HTMLCanvasElement>(null);
  const sculptCanvasRef = useRef<HTMLCanvasElement>(null);
  const sculptHandlesElRef = useRef<HTMLDivElement>(null);
  const sculptSigmaSliderRef = useRef<HTMLInputElement>(null);
  const sculptSigmaValueElRef = useRef<HTMLSpanElement>(null);
  const sculptZoomValueElRef = useRef<HTMLSpanElement>(null);
  const sculptMarkerSizeValueElRef = useRef<HTMLSpanElement>(null);
  const sculptBaseWidthPxRef = useRef(0); // the canvas's natural (zoom=1) displayed width in px — see initSculpt()
  const sculptRegionTogglesElRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadStarted = useRef(false);
  const lastImageRef = useRef<HTMLImageElement | null>(null);
  // Reused across renders instead of re-creating the WebGL context every
  // click — see renderMesh() below.
  const pixiAppRef = useRef<PixiApplication | null>(null);
  // Shared with renderSmile() below, so it can warp the same mesh/vertices
  // renderMesh() already built instead of rebuilding everything.
  const currentMeshRef = useRef<PixiMeshSimple | null>(null);
  const currentVerticesRef = useRef<Float32Array | null>(null); // ORIGINAL (unwarped) positions — renderSmile() always displaces a fresh copy of these, never this array itself, so re-clicking "Smile" starts from the true original rather than compounding.
  // Sculpt-only state — deliberately plain refs, not React state: dragging
  // needs to re-render the canvas on every pointermove, and routing that
  // through setState/re-render would be both slower and pointless (nothing
  // here is declarative JSX, it's all imperative canvas drawing and DOM
  // text updates, same as exploring/face-landmarks/index.html).
  const sculptHandlesRef = useRef<{ vertexIndex: number; targetX: number; targetY: number }[]>([]);
  const sculptActiveHandleRef = useRef<number | null>(null);
  const sculptInterOcularRef = useRef(0);
  const sculptSigmaRatioRef = useRef(0.22); // matches Smile/Sad's verified-safe default
  const sculptPointerEventsWiredRef = useRef(false);
  // category name -> Set of vertex indices, rebuilt in initSculpt()
  const sculptCategoryIndicesRef = useRef<Record<string, Set<number>>>({});
  // which categories' points are currently visible/grabbable — empty until
  // the user ticks one, per the explicit ask: nothing shown until opted in
  const sculptEnabledCategoriesRef = useRef<Set<string>>(new Set());
  // Available-point dots AND handle markers — off lets you preview the
  // clean warped result without pink dots/lines cluttering it. Handles
  // themselves aren't cleared by this, only what gets drawn.
  const sculptShowMarkersRef = useRef(true);
  // Multiplies both the "available point" dot radius and the handle marker
  // radius — a marker at its default size can cover the very detail it's
  // supposed to help position precisely, especially once zoomed in.
  const sculptMarkerSizeRatioRef = useRef(1);
  const [sculptReady, setSculptReady] = useState(false);

  // Phase 5c: reference image — a second, independent photo (e.g. a
  // screenshot of a filter being reverse-engineered by observation, see
  // FACE_FILTERS_ROADMAP.md) shown either beside or blended under the
  // working sculpt canvas. Gets its own mesh via the same landmarker and
  // drawRegionColoredMesh() the main canvas uses, so a landmark's
  // color/position can be read off directly instead of eyeballed.
  const referenceImageRef = useRef<HTMLImageElement | null>(null);
  const referenceCanvasRef = useRef<HTMLCanvasElement>(null);
  const referenceFileInputRef = useRef<HTMLInputElement>(null);
  const [referenceDragging, setReferenceDragging] = useState(false);
  const [referenceReady, setReferenceReady] = useState(false);
  const [referenceLayout, setReferenceLayout] = useState<'side-by-side' | 'overlay'>(
    'side-by-side',
  );
  // The WORKING photo's opacity when composited over the reference in
  // overlay mode — a ref like the other sculpt sliders, not state, since it
  // drives sculptRender() directly rather than any JSX.
  const referenceOpacityRef = useRef(0.6);
  const referenceOpacityValueElRef = useRef<HTMLSpanElement>(null);
  // The reference photo's own detection result — needed by Phase 5d's
  // match-reference algorithm below, not just for drawing its mesh.
  const referenceResultRef = useRef<FaceLandmarkerResult | null>(null);

  // Phase 5d: match reference — see the doc comment on matchReference()
  // below for the full algorithm.
  const matchStrengthRef = useRef(1);
  const matchStrengthValueElRef = useRef<HTMLSpanElement>(null);
  const matchCanvasRef = useRef<HTMLCanvasElement>(null);
  const [matchStatus, setMatchStatus] = useState<string | null>(null);

  // Phase 6 — Snap Camera Kit, applying a real Snap Lens to the STILL photo
  // above. See runCameraKit() below for what this is testing and why.
  //
  // Credentials come from the gitignored root `.env` (see `.env.example`
  // for how to obtain them). They are VITE_-prefixed and therefore bundled
  // into the client — inherent to Camera Kit, whose Web SDK authenticates
  // from the browser, and the reason Snap Developer Terms §5.a makes
  // keeping `.env` out of git contractual rather than merely tidy. The
  // fields below stay editable so a different token or group can be tried
  // without a server restart.
  const cameraKitCanvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraKitToken, setCameraKitToken] = useState(
    (import.meta.env.VITE_CAMERA_KIT_API_TOKEN as string | undefined) ?? '',
  );
  const [cameraKitGroupId, setCameraKitGroupId] = useState(
    (import.meta.env.VITE_CAMERA_KIT_LENS_GROUP_ID as string | undefined) ?? '',
  );
  const [cameraKitStatus, setCameraKitStatus] = useState<string | null>(null);
  const [cameraKitBusy, setCameraKitBusy] = useState(false);
  // The Lenses discovered in the group, and which one is selected. A Lens ID
  // is deliberately NOT typed in by hand: with only a group id (what the
  // portal calls the "Demo Lens Group"), `loadLensGroups` returns every Lens
  // in it, so the picker below is populated from the account itself. That
  // also makes cycling through Lenses cheap — which is the actual experiment
  // here, since whether a given Lens survives a still frame varies per Lens.
  const [cameraKitLenses, setCameraKitLenses] = useState<{ id: string; name: string }[]>([]);
  const [cameraKitSelectedLensId, setCameraKitSelectedLensId] = useState('');
  // The live session, kept so its result stays on screen (destroying it
  // blanks the canvas — see the note in runCameraKit) while still
  // guaranteeing only one exists: the next run tears this one down, and so
  // does unmounting.
  const cameraKitSessionRef = useRef<{ pause: () => void; destroy: () => Promise<void> } | null>(
    null,
  );
  useEffect(() => {
    return () => {
      const session = cameraKitSessionRef.current;
      cameraKitSessionRef.current = null;
      if (session) {
        session.pause();
        void session.destroy();
      }
    };
  }, []);

  // Lazily fetches the (fairly large) MediaPipe module only once this
  // screen actually mounts, and only once even under StrictMode's double
  // effect — every other user's bundle is completely untouched by this,
  // since nothing here is imported until someone is actually on this route.
  if (!loadStarted.current && isTester(session?.user?.email)) {
    loadStarted.current = true;
    void (async () => {
      try {
        const mod = await import(/* @vite-ignore */ `${CDN_BASE}/vision_bundle.mjs`);
        const vision = await mod.FilesetResolver.forVisionTasks(`${CDN_BASE}/wasm`);
        const landmarker = await mod.FaceLandmarker.createFromOptions(vision, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
          runningMode: 'IMAGE',
          numFaces: 2, // >1 so a group photo visibly reports "2 faces" rather than silently picking one
          outputFaceBlendshapes: true,
          outputFacialTransformationMatrixes: false,
        });
        setLoad({ kind: 'ready', landmarker: landmarker as FaceLandmarker });
        setStatus('Ready. Choose a photo.');
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setLoad({ kind: 'error', message });
        setStatus(`Failed to load MediaPipe: ${message}`);
      }
    })();
  }

  if (isPending) {
    return (
      <main className="screen">
        <p className="muted">Checking sign-in status…</p>
      </main>
    );
  }

  // Same "no such page" shape the rest of the app uses for a screen someone
  // isn't allowed to see — see lib/testers.ts for why this check alone is
  // the right bar for a screen with no API of its own.
  if (!isTester(session?.user?.email)) {
    return <Navigate to="/" replace />;
  }

  async function handleFile(file: File) {
    if (load.kind !== 'ready') return;
    setStatus('Detecting…');
    setMeshStatus(null);
    setMeshReady(false);
    setSmileStatus(null);
    setSadStatus(null);
    setSculptReady(false);
    sculptHandlesRef.current = [];
    sculptActiveHandleRef.current = null;

    const image = await loadImage(file);
    lastImageRef.current = image;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // detect(), not detectForVideo() — IMAGE mode, one real call per
    // uploaded photo. See the roadmap's Phase 0/1 for why that's the right
    // API for a still upload rather than the webcam-tuned one most
    // tutorials show.
    const result = load.landmarker.detect(image);
    setLastResult(result);
    drawRegionColoredMesh(ctx, canvas, image, result);
    setStatus(formatReport(result));
  }

  async function handleReferenceFile(file: File) {
    if (load.kind !== 'ready') return;
    const image = await loadImage(file);
    referenceImageRef.current = image;
    const canvas = referenceCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // detect(), IMAGE mode — same reasoning as the main photo. A reference
    // image with no face, or more than one, still renders; it just won't
    // have a mesh drawn on it (0 faces) or will use only the first
    // detected face (>1 faces), same as everywhere else on this screen.
    const result = load.landmarker.detect(image);
    referenceResultRef.current = result; // needed by Phase 5d's match-reference algorithm, not just for drawing
    drawRegionColoredMesh(ctx, canvas, image, result);
    setReferenceReady(true);
    sculptRender(); // safe no-op if the mesh hasn't been rendered yet
  }

  // --- Phase 3: mesh render (PixiJS) --------------------------------------
  // Renders the same photo as a textured triangle mesh, every vertex left at
  // its original landmark position — no warping. The roadmap's done-bar for
  // this phase: pixel-identical to the source photo. Ported from
  // exploring/face-landmarks/index.html, where this was already verified
  // (0.00% mismatch across ~455,000 compared pixels) — see that folder's
  // README for the full account, including a real bug hit along the way:
  // loading PixiJS from a CDN's auto-bundled ESM endpoint (jsDelivr's
  // "+esm") silently breaks it, because PixiJS internally `import()`s its
  // GPU/GL backend at runtime and the CDN re-bundles that inner import as a
  // disconnected module. That's moot here — this is a real npm dependency
  // (`pixi.js`), bundled by Vite like everything else in this app, so
  // there's no CDN re-bundling step to break it. Still lazy-loaded via
  // dynamic import() so it's code-split into its own chunk and costs
  // nothing for every visitor who never opens this screen.
  async function renderMesh() {
    const image = lastImageRef.current;
    const meshCanvas = meshCanvasRef.current;
    if (!image || !meshCanvas || !lastResult || lastResult.faceLandmarks.length === 0) return;

    setMeshBusy(true);
    setMeshStatus('Loading PixiJS…');

    const PIXI = await import('pixi.js');

    const width = image.naturalWidth;
    const height = image.naturalHeight;
    const landmarks = lastResult.faceLandmarks[0]; // first face only, for this test
    if (!landmarks) return;

    // Vertex positions in PIXEL space (what the mesh draws at), UVs in the
    // same normalized 0-1 space MediaPipe already gives us (what the mesh
    // samples from the texture) — landmark.x/.y serve directly as UVs since
    // the photo itself is the texture.
    const vertexCount = 468; // base mesh only — iris points (468-477) aren't part of the tesselation
    const vertices = new Float32Array(vertexCount * 2);
    const uvs = new Float32Array(vertexCount * 2);
    for (let i = 0; i < vertexCount; i++) {
      const p = landmarks[i];
      if (!p) continue;
      vertices[i * 2] = p.x * width;
      vertices[i * 2 + 1] = p.y * height;
      uvs[i * 2] = p.x;
      uvs[i * 2 + 1] = p.y;
    }
    const indices = new Uint32Array(FACEMESH_TESSELATION_FLAT);

    meshCanvas.width = width;
    meshCanvas.height = height;

    let pixiApp = pixiAppRef.current;
    if (!pixiApp) {
      pixiApp = new PIXI.Application();
      // preserveDrawingBuffer: true — the verification step below reads the
      // canvas back via drawImage()/getImageData() right after render(),
      // which isn't reliably defined on a WebGL canvas without this.
      await pixiApp.init({
        canvas: meshCanvas,
        width,
        height,
        antialias: true,
        backgroundAlpha: 0,
        preserveDrawingBuffer: true,
      });
      pixiAppRef.current = pixiApp;
    } else {
      pixiApp.renderer.resize(width, height);
    }
    pixiApp.stage.removeChildren();

    const texture = PIXI.Texture.from(image);
    const mesh = new PIXI.MeshSimple({ texture, vertices, uvs, indices });
    pixiApp.stage.addChild(mesh);
    pixiApp.render();
    currentMeshRef.current = mesh;
    currentVerticesRef.current = vertices;

    // --- Verify: is this actually pixel-identical? --------------------
    // Not PixiJS's own renderer.extract.pixels({target: mesh}) — tried
    // that first (in exploring/face-landmarks/), and it came back with
    // every pixel transparent despite the mesh visibly having rendered,
    // apparently unrelated to the CDN issue above. Reading back the
    // canvas Pixi already drew into is simpler, and tests the actual
    // thing on screen rather than a separate extraction code path.
    const renderedCanvas = document.createElement('canvas');
    renderedCanvas.width = width;
    renderedCanvas.height = height;
    const renderedCtx = renderedCanvas.getContext('2d');
    const origCanvas = document.createElement('canvas');
    origCanvas.width = width;
    origCanvas.height = height;
    const origCtx = origCanvas.getContext('2d');
    if (!renderedCtx || !origCtx) return;
    renderedCtx.drawImage(meshCanvas, 0, 0);
    origCtx.drawImage(image, 0, 0);
    const rendered = renderedCtx.getImageData(0, 0, width, height).data;
    const original = origCtx.getImageData(0, 0, width, height).data;

    let coveredPixels = 0;
    let mismatchedPixels = 0; // per-channel diff > 8, a small allowance for antialiasing at triangle edges
    let totalDiff = 0;
    for (let i = 0; i < rendered.length; i += 4) {
      const alpha = rendered[i + 3];
      if (alpha === undefined || alpha < 10) continue; // outside the mesh's triangulated region
      coveredPixels++;
      const dr = Math.abs((rendered[i] ?? 0) - (original[i] ?? 0));
      const dg = Math.abs((rendered[i + 1] ?? 0) - (original[i + 1] ?? 0));
      const db = Math.abs((rendered[i + 2] ?? 0) - (original[i + 2] ?? 0));
      totalDiff += dr + dg + db;
      if (dr > 8 || dg > 8 || db > 8) mismatchedPixels++;
    }

    const coveragePct = ((coveredPixels / (width * height)) * 100).toFixed(1);
    const mismatchPct =
      coveredPixels > 0 ? ((mismatchedPixels / coveredPixels) * 100).toFixed(2) : 'n/a';
    const avgDiff = coveredPixels > 0 ? (totalDiff / coveredPixels / 3).toFixed(2) : 'n/a';

    setMeshStatus(
      [
        `Mesh: 468 vertices, ${FACEMESH_TESSELATION_FLAT.length / 3} triangles.`,
        `Face region covers ${coveragePct}% of the canvas (${coveredPixels} px).`,
        `Pixel match: ${mismatchPct}% of pixels differ by more than 8/255 per channel. Average channel difference: ${avgDiff}/255.`,
        Number(mismatchPct) < 2
          ? '✅ Reads as pixel-identical — Phase 3 done-criterion met.'
          : '⚠️ More mismatch than expected — inspect visually before trusting this.',
      ].join('\n'),
    );
    setMeshBusy(false);
    setMeshReady(true);
    initSculpt();
  }

  // --- Phase 4: first warp (smile) ----------------------------------------
  // Ported from exploring/face-landmarks/index.html, where this was already
  // verified — see that folder's README for the full account, including a
  // real MediaPipe-based objective check (not just eyeballing): re-detecting
  // the warped, composited image and confirming MediaPipe's own
  // `mouthSmileLeft`/`mouthSmileRight` blendshape scores went up.
  //
  // A smooth, falloff-weighted displacement, not a raw two-point pull —
  // moving only the two mouth-corner vertices while leaving their immediate
  // neighbors untouched is exactly the "smear" failure mode the roadmap
  // warns about (a torn triangle at the mouth corner). Every vertex moves by
  // a WEIGHTED SUM of the two corners' target displacement, weighted by a
  // Gaussian of its distance from each corner's ORIGINAL position — vertices
  // right at the corner move (nearly) the full amount, nearby lip/cheek
  // vertices follow along at a diminishing fraction, and anything more than
  // ~1.5 inter-ocular distances away barely moves at all. This is a
  // simplified radial-basis-function warp — not full Moving Least Squares
  // (the roadmap's more sophisticated alternative), but the same underlying
  // idea: smooth, not a hard cut.
  async function renderSmile() {
    const image = lastImageRef.current;
    const mesh = currentMeshRef.current;
    const originalVertices = currentVerticesRef.current;
    const meshCanvas = meshCanvasRef.current;
    const smileCanvas = smileCanvasRef.current;
    const pixiApp = pixiAppRef.current;
    if (
      !image ||
      !mesh ||
      !originalVertices ||
      !meshCanvas ||
      !smileCanvas ||
      !pixiApp ||
      !lastResult ||
      load.kind !== 'ready'
    ) {
      return;
    }
    const landmarks = lastResult.faceLandmarks[0];
    if (!landmarks) return;

    setSmileBusy(true);
    setSmileStatus('Warping…');

    const width = image.naturalWidth;
    const height = image.naturalHeight;

    // Scale every distance/displacement by inter-ocular distance so this
    // looks the same on a close-up portrait and a small face in a group
    // photo, instead of a fixed pixel amount that's huge on one and
    // invisible on the other.
    const leftEye = landmarks[33]; // "left eye outer" per NAMED_LANDMARKS above
    const rightEye = landmarks[263];
    const mouthLeft = landmarks[61]; // "mouth left"
    const mouthRight = landmarks[291]; // "mouth right"
    if (!leftEye || !rightEye || !mouthLeft || !mouthRight) return;

    const interOcular = Math.hypot(
      (rightEye.x - leftEye.x) * width,
      (rightEye.y - leftEye.y) * height,
    );
    const mouthCenterX = ((mouthLeft.x + mouthRight.x) / 2) * width;

    const liftMag = interOcular * 0.11; // upward
    const outMag = interOcular * 0.035; // slightly away from mouth center — AU12 pulls up AND out, not straight up
    // sigma was originally interOcular * 0.55 — WRONG, found live (see
    // exploring/face-landmarks/README.md for the full account): at the
    // face-oval boundary (roughly 0.6x interOcular from a mouth corner,
    // e.g. the jaw/chin), that sigma still gave ~55% weight, moving the
    // boundary ~37px. Since this mesh is a hard-edged cutout pasted over the
    // SAME static photo, shifting that boundary — not just the mouth — is
    // exactly what made the result look like a second, slightly offset face
    // pasted on top ("a mask"), not a smile. Shrunk so the falloff is
    // negligible (~1px) by the time it reaches the oval. (A hard pin on the
    // oval vertices was tried too and reverted — it's a SHARPER
    // discontinuity than the shrunk Gaussian and folded 2-3 triangles right
    // at the pin edge, caught by the triangle-inversion check below.)
    const sigma = interOcular * 0.22;

    const controlPointFor = (landmark: Landmark) => {
      const px = landmark.x * width;
      const py = landmark.y * height;
      const outSign = px - mouthCenterX >= 0 ? 1 : -1; // which side of the mouth this corner is on
      return { x: px, y: py, dx: outSign * outMag, dy: -liftMag };
    };
    const controlPoints = [controlPointFor(mouthLeft), controlPointFor(mouthRight)];

    const warped = new Float32Array(originalVertices.length);
    for (let i = 0; i < originalVertices.length; i += 2) {
      const x = originalVertices[i] ?? 0;
      const y = originalVertices[i + 1] ?? 0;
      let dx = 0;
      let dy = 0;
      for (const cp of controlPoints) {
        const d2 = (x - cp.x) ** 2 + (y - cp.y) ** 2;
        const weight = Math.exp(-d2 / (2 * sigma * sigma));
        dx += weight * cp.dx;
        dy += weight * cp.dy;
      }
      warped[i] = x + dx;
      warped[i + 1] = y + dy;
    }

    // --- Validate 1: no inverted (self-intersecting) triangles -----------
    // A triangle whose signed area flips sign between the original and
    // warped positions has folded over itself — the actual geometric
    // definition of a "tear/smear", not just a subjective eyeball call.
    const signedArea = (verts: Float32Array, ia: number, ib: number, ic: number) => {
      const ax = verts[ia * 2] ?? 0;
      const ay = verts[ia * 2 + 1] ?? 0;
      const bx = verts[ib * 2] ?? 0;
      const by = verts[ib * 2 + 1] ?? 0;
      const cx = verts[ic * 2] ?? 0;
      const cy = verts[ic * 2 + 1] ?? 0;
      return (bx - ax) * (cy - ay) - (cx - ax) * (by - ay);
    };
    let invertedTriangles = 0;
    for (let i = 0; i < FACEMESH_TESSELATION_FLAT.length; i += 3) {
      const a = FACEMESH_TESSELATION_FLAT[i] ?? 0;
      const b = FACEMESH_TESSELATION_FLAT[i + 1] ?? 0;
      const c = FACEMESH_TESSELATION_FLAT[i + 2] ?? 0;
      const before = signedArea(originalVertices, a, b, c);
      const after = signedArea(warped, a, b, c);
      if (Math.sign(before) !== Math.sign(after)) invertedTriangles++;
    }

    mesh.vertices = warped;
    pixiApp.render();

    // Composite onto the original photo — the mesh only covers the
    // triangulated face region, so the rest of the photo (hair, ears,
    // background) needs to show through around it for this to read as an
    // actual warped PHOTO, not a floating face cutout.
    smileCanvas.width = width;
    smileCanvas.height = height;
    const smileCtx = smileCanvas.getContext('2d');
    if (!smileCtx) return;
    smileCtx.clearRect(0, 0, width, height);
    smileCtx.drawImage(image, 0, 0);
    smileCtx.drawImage(meshCanvas, 0, 0);

    // --- Validate 2: does MediaPipe itself now see more of a smile? ------
    // The strongest available check, and independent of anything this
    // screen assumes about its own warp: feed the warped, composited image
    // back through the SAME face landmarker used for detection, and compare
    // its own `mouthSmileLeft`/`mouthSmileRight` blendshape scores
    // (confirmed to exist among the real 52 categories, not assumed) before
    // and after. If MediaPipe's own model doesn't read more smile in the
    // output than the input, the warp didn't work, regardless of how it
    // looks.
    const before = lastResult.faceBlendshapes?.[0]?.categories ?? [];
    const beforeSmile =
      (before.find((c) => c.categoryName === 'mouthSmileLeft')?.score ?? 0) +
      (before.find((c) => c.categoryName === 'mouthSmileRight')?.score ?? 0);

    const afterResult = load.landmarker.detect(smileCanvas);
    const afterCats = afterResult.faceBlendshapes?.[0]?.categories ?? [];
    const afterSmile =
      (afterCats.find((c) => c.categoryName === 'mouthSmileLeft')?.score ?? 0) +
      (afterCats.find((c) => c.categoryName === 'mouthSmileRight')?.score ?? 0);
    const faceStillDetected = afterResult.faceLandmarks.length > 0;

    setSmileStatus(
      [
        `Displacement: ${liftMag.toFixed(1)}px up, ${outMag.toFixed(1)}px out at each mouth corner (scaled from a ${interOcular.toFixed(1)}px inter-ocular distance), falloff radius ${sigma.toFixed(1)}px.`,
        `Triangle check: ${invertedTriangles} of ${FACEMESH_TESSELATION_FLAT.length / 3} triangles inverted (folded over themselves).`,
        `MediaPipe's own mouthSmileLeft+Right score: ${beforeSmile.toFixed(3)} before → ${afterSmile.toFixed(3)} after (re-detected on the warped, composited image, not assumed).`,
        `Face still detected after warping: ${faceStillDetected ? 'yes' : 'NO — warp broke detection'}.`,
        invertedTriangles === 0 && faceStillDetected && afterSmile > beforeSmile * 1.5
          ? '✅ Reads as a smile, not a smear — Phase 4 done-criterion met.'
          : '⚠️ Inspect visually — one or more checks did not clearly pass.',
      ].join('\n'),
    );
    setSmileBusy(false);
  }

  // --- Phase 5: expression authoring — sad --------------------------------
  // Ported from exploring/face-landmarks/index.html, where this was already
  // verified — see that folder's README for the full account, including a
  // note on why the automated pass bar had to be calibrated differently from
  // smile's (see the comment before the blendshape check below).
  //
  // Same Gaussian-falloff radial-basis warp as renderSmile() above (same
  // sigma, verified there not to disturb the face-oval boundary), but with
  // three control-point PAIRS instead of one:
  //   - Mouth corners (61, 291): pulled DOWN and slightly IN — AU15, lip
  //     corner depressor.
  //   - Inner eyebrows (55 right, 285 left — confirmed live against a real
  //     photo by measuring which eyebrow landmark sits closest to the nose
  //     bridge, not assumed): pulled UP and slightly IN — AU1 (inner brow
  //     raiser) + AU4 (brow lowerer/knit) combined. The roadmap calls this
  //     "the single most 'sad-reading' deformation that exists."
  //   - Outer eye corners (33, 263): a small downward droop — not a named
  //     FACS Action Unit on its own, added because a plain frown alone read
  //     as barely-there; eye corners that droop (rather than narrow, which
  //     is the smile's eye behavior) is the visual cue that reads as
  //     "worried"/"desperate" rather than just "not smiling."
  async function renderSad() {
    const image = lastImageRef.current;
    const mesh = currentMeshRef.current;
    const originalVertices = currentVerticesRef.current;
    const meshCanvas = meshCanvasRef.current;
    const sadCanvas = sadCanvasRef.current;
    const pixiApp = pixiAppRef.current;
    if (
      !image ||
      !mesh ||
      !originalVertices ||
      !meshCanvas ||
      !sadCanvas ||
      !pixiApp ||
      !lastResult ||
      load.kind !== 'ready'
    ) {
      return;
    }
    const landmarks = lastResult.faceLandmarks[0];
    if (!landmarks) return;

    setSadBusy(true);
    setSadStatus('Warping…');

    const width = image.naturalWidth;
    const height = image.naturalHeight;

    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    const mouthLeft = landmarks[61];
    const mouthRight = landmarks[291];
    const innerBrowRight = landmarks[55];
    const innerBrowLeft = landmarks[285];
    const noseBridge = landmarks[6];
    if (
      !leftEye ||
      !rightEye ||
      !mouthLeft ||
      !mouthRight ||
      !innerBrowRight ||
      !innerBrowLeft ||
      !noseBridge
    ) {
      return;
    }

    const interOcular = Math.hypot(
      (rightEye.x - leftEye.x) * width,
      (rightEye.y - leftEye.y) * height,
    );
    const mouthCenterX = ((mouthLeft.x + mouthRight.x) / 2) * width;
    const noseX = noseBridge.x * width;

    const mouthDownMag = interOcular * 0.13;
    const mouthInMag = interOcular * 0.04;
    const browUpMag = interOcular * 0.12;
    const browInMag = interOcular * 0.03;
    const eyeDroopMag = interOcular * 0.035;
    const sigma = interOcular * 0.22; // same verified-safe falloff radius as the smile warp

    const point = (landmark: Landmark) => ({ x: landmark.x * width, y: landmark.y * height });
    const towardCenterSign = (px: number, centerX: number) => (px - centerX >= 0 ? -1 : 1);

    const mL = point(mouthLeft);
    const mR = point(mouthRight);
    const bR = point(innerBrowRight);
    const bL = point(innerBrowLeft);
    const eyeOuterLeft = point(leftEye); // landmark 33, "left eye outer" per NAMED_LANDMARKS
    const eyeOuterRight = point(rightEye); // landmark 263, "right eye outer"

    const controlPoints = [
      { ...mL, dx: towardCenterSign(mL.x, mouthCenterX) * mouthInMag, dy: mouthDownMag },
      { ...mR, dx: towardCenterSign(mR.x, mouthCenterX) * mouthInMag, dy: mouthDownMag },
      { ...bR, dx: towardCenterSign(bR.x, noseX) * browInMag, dy: -browUpMag },
      { ...bL, dx: towardCenterSign(bL.x, noseX) * browInMag, dy: -browUpMag },
      { ...eyeOuterLeft, dx: 0, dy: eyeDroopMag },
      { ...eyeOuterRight, dx: 0, dy: eyeDroopMag },
    ];

    const warped = new Float32Array(originalVertices.length);
    for (let i = 0; i < originalVertices.length; i += 2) {
      const x = originalVertices[i] ?? 0;
      const y = originalVertices[i + 1] ?? 0;
      let dx = 0;
      let dy = 0;
      for (const cp of controlPoints) {
        const d2 = (x - cp.x) ** 2 + (y - cp.y) ** 2;
        const weight = Math.exp(-d2 / (2 * sigma * sigma));
        dx += weight * cp.dx;
        dy += weight * cp.dy;
      }
      warped[i] = x + dx;
      warped[i + 1] = y + dy;
    }

    // --- Validate 1: no inverted (self-intersecting) triangles -----------
    const signedArea = (verts: Float32Array, ia: number, ib: number, ic: number) => {
      const ax = verts[ia * 2] ?? 0;
      const ay = verts[ia * 2 + 1] ?? 0;
      const bx = verts[ib * 2] ?? 0;
      const by = verts[ib * 2 + 1] ?? 0;
      const cx = verts[ic * 2] ?? 0;
      const cy = verts[ic * 2 + 1] ?? 0;
      return (bx - ax) * (cy - ay) - (cx - ax) * (by - ay);
    };
    let invertedTriangles = 0;
    for (let i = 0; i < FACEMESH_TESSELATION_FLAT.length; i += 3) {
      const a = FACEMESH_TESSELATION_FLAT[i] ?? 0;
      const b = FACEMESH_TESSELATION_FLAT[i + 1] ?? 0;
      const c = FACEMESH_TESSELATION_FLAT[i + 2] ?? 0;
      const before = signedArea(originalVertices, a, b, c);
      const after = signedArea(warped, a, b, c);
      if (Math.sign(before) !== Math.sign(after)) invertedTriangles++;
    }

    mesh.vertices = warped;
    pixiApp.render();

    sadCanvas.width = width;
    sadCanvas.height = height;
    const sadCtx = sadCanvas.getContext('2d');
    if (!sadCtx) return;
    sadCtx.clearRect(0, 0, width, height);
    sadCtx.drawImage(image, 0, 0);
    sadCtx.drawImage(meshCanvas, 0, 0);

    // --- Validate 2: does MediaPipe itself now see more sadness? ---------
    // Calibrated differently from the smile check on purpose: smile
    // produced a dramatic score jump (0.001 -> 0.851) at comparable warp
    // strength. Sad does not — doubling the geometric magnitude in testing
    // barely moved the score (non-monotonic across runs), while the VISUAL
    // read clearly got more convincing. Likely explanation: MediaPipe's
    // blendshape model was trained on natural expressions where sadness
    // correlates with many cues at once (skin texture, eye moisture, subtle
    // color shifts) that a pure landmark warp doesn't touch, whereas a
    // smile's signature is more purely geometric. So: any real increase,
    // not a specific threshold, plus a clean triangle check, is the bar
    // here — visual inspection carries more weight for this expression than
    // it did for smile.
    const before = lastResult.faceBlendshapes?.[0]?.categories ?? [];
    const beforeSad =
      (before.find((c) => c.categoryName === 'browInnerUp')?.score ?? 0) +
      (before.find((c) => c.categoryName === 'mouthFrownLeft')?.score ?? 0) +
      (before.find((c) => c.categoryName === 'mouthFrownRight')?.score ?? 0);

    const afterResult = load.landmarker.detect(sadCanvas);
    const afterCats = afterResult.faceBlendshapes?.[0]?.categories ?? [];
    const afterSad =
      (afterCats.find((c) => c.categoryName === 'browInnerUp')?.score ?? 0) +
      (afterCats.find((c) => c.categoryName === 'mouthFrownLeft')?.score ?? 0) +
      (afterCats.find((c) => c.categoryName === 'mouthFrownRight')?.score ?? 0);
    const faceStillDetected = afterResult.faceLandmarks.length > 0;

    setSadStatus(
      [
        `Mouth: ${mouthDownMag.toFixed(1)}px down, ${mouthInMag.toFixed(1)}px in. Brows: ${browUpMag.toFixed(1)}px up, ${browInMag.toFixed(1)}px in. Eyes: ${eyeDroopMag.toFixed(1)}px droop. Falloff radius ${sigma.toFixed(1)}px.`,
        `Triangle check: ${invertedTriangles} of ${FACEMESH_TESSELATION_FLAT.length / 3} triangles inverted (folded over themselves).`,
        `MediaPipe's own browInnerUp+mouthFrownLeft+mouthFrownRight score: ${beforeSad.toFixed(3)} before → ${afterSad.toFixed(3)} after (re-detected on the warped, composited image, not assumed).`,
        `Face still detected after warping: ${faceStillDetected ? 'yes' : 'NO — warp broke detection'}.`,
        invertedTriangles === 0 && faceStillDetected && afterSad > beforeSad
          ? '✅ No tears/geometry breakage, and MediaPipe reads more sadness than the original — judge how convincing it is visually (this check is weaker for sad than it was for smile, see comment above).'
          : '⚠️ Inspect visually — one or more checks did not clearly pass.',
      ].join('\n'),
    );
    setSadBusy(false);
  }

  // --- Phase 5b: interactive sculpt ---------------------------------------
  // Ported from exploring/face-landmarks/index.html — same underlying math
  // as renderSmile()/renderSad() above (every vertex's displacement is a
  // Gaussian-weighted sum over a set of "control points"), generalized so
  // the control points come from clicking and dragging instead of being
  // hardcoded. Each drag creates or moves a "handle": {vertexIndex,
  // targetX, targetY} — the vertex grabbed, and where it's currently been
  // dragged to. Re-warping and re-rendering on every pointermove is what
  // makes it feel live; see sculptRender() below.
  function initSculpt() {
    const image = lastImageRef.current;
    const canvas = sculptCanvasRef.current;
    if (!image || !canvas || !lastResult || !currentVerticesRef.current) return;
    const landmarks = lastResult.faceLandmarks[0];
    const leftEye = landmarks?.[33];
    const rightEye = landmarks?.[263];
    if (!leftEye || !rightEye) return;

    sculptInterOcularRef.current = Math.hypot(
      (rightEye.x - leftEye.x) * image.naturalWidth,
      (rightEye.y - leftEye.y) * image.naturalHeight,
    );
    sculptHandlesRef.current = [];
    sculptActiveHandleRef.current = null;
    canvas.style.maxWidth = ''; // clear any zoom left over from a previous photo
    canvas.style.width = '';
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    // The zoom slider needs an absolute pixel width to multiply, not a
    // percentage — found live: this canvas sits in a `.screen` flex column
    // (see components.css), and a percentage-width child of a flex item
    // resolves against 0 there instead of the item's own stretched width
    // (a real flexbox/percentage interaction, not a typo). Captured once
    // here, right after the natural (max-width: 100%, un-zoomed) layout is
    // established, forcing a synchronous layout read.
    sculptBaseWidthPxRef.current = canvas.getBoundingClientRect().width;
    sculptCategoryIndicesRef.current = buildSculptCategoryIndices();
    sculptEnabledCategoriesRef.current = new Set(); // nothing shown until the user opts a region in
    renderSculptRegionToggles();
    wireSculptPointerEvents(); // idempotent — see the ref guard inside
    setSculptReady(true);
    sculptRender();
  }

  function resetSculpt() {
    sculptHandlesRef.current = [];
    sculptActiveHandleRef.current = null;
    sculptRender();
  }

  function buildSculptCategoryIndices(): Record<string, Set<number>> {
    const categories: Record<string, Set<number>> = {};
    for (const region of FACE_REGIONS) {
      if (region.label === 'Iris') continue; // see SCULPT_CATEGORY_COLORS's comment above
      categories[region.label] = new Set(region.indices);
    }
    const namedUnion = new Set<number>();
    for (const region of FACE_REGIONS) {
      for (const i of region.indices) namedUnion.add(i);
    }

    const originalVertices = currentVerticesRef.current;
    const eyebrowIndices = FACE_REGIONS.find((r) => r.label === 'Eyebrows')?.indices;
    const lipsIndices = FACE_REGIONS.find((r) => r.label === 'Lips')?.indices;
    if (!originalVertices || !eyebrowIndices || !lipsIndices) return categories;

    let browYSum = 0;
    for (const i of eyebrowIndices) browYSum += originalVertices[i * 2 + 1] ?? 0;
    const browAvgY = browYSum / eyebrowIndices.size;
    let mouthYSum = 0;
    for (const i of lipsIndices) mouthYSum += originalVertices[i * 2 + 1] ?? 0;
    const mouthAvgY = mouthYSum / lipsIndices.size;

    categories['Forehead'] = new Set();
    categories['Cheeks'] = new Set();
    categories['Jaw'] = new Set();
    for (let i = 0; i < 468; i++) {
      if (namedUnion.has(i)) continue;
      const y = originalVertices[i * 2 + 1] ?? 0;
      if (y < browAvgY) categories['Forehead'].add(i);
      else if (y > mouthAvgY) categories['Jaw'].add(i);
      else categories['Cheeks'].add(i);
    }
    return categories;
  }

  function renderSculptRegionToggles() {
    const el = sculptRegionTogglesElRef.current;
    if (!el) return;
    el.innerHTML = '';
    for (const name of Object.keys(SCULPT_CATEGORY_COLORS)) {
      const label = document.createElement('label');
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = sculptEnabledCategoriesRef.current.has(name);
      checkbox.addEventListener('change', () => {
        if (checkbox.checked) sculptEnabledCategoriesRef.current.add(name);
        else sculptEnabledCategoriesRef.current.delete(name);
        sculptRender();
      });
      const swatch = document.createElement('span');
      swatch.style.display = 'inline-block';
      swatch.style.width = '10px';
      swatch.style.height = '10px';
      swatch.style.borderRadius = '50%';
      swatch.style.border = '1px solid rgba(0, 0, 0, 0.4)';
      swatch.style.background = SCULPT_CATEGORY_COLORS[name] ?? '#ffffff';
      const count = sculptCategoryIndicesRef.current[name]?.size ?? 0;
      label.appendChild(checkbox);
      label.appendChild(swatch);
      label.appendChild(document.createTextNode(` ${name} (${count})`));
      el.appendChild(label);
    }
  }

  function enabledVertexIndices(): Set<number> {
    const result = new Set<number>();
    for (const name of sculptEnabledCategoriesRef.current) {
      const set = sculptCategoryIndicesRef.current[name];
      if (!set) continue;
      for (const i of set) result.add(i);
    }
    return result;
  }

  function wireSculptPointerEvents() {
    if (sculptPointerEventsWiredRef.current) return;
    const canvas = sculptCanvasRef.current;
    if (!canvas) return;
    sculptPointerEventsWiredRef.current = true;

    const canvasPointFromEvent = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((e.clientX - rect.left) / rect.width) * canvas.width,
        y: ((e.clientY - rect.top) / rect.height) * canvas.height,
      };
    };
    // Grab radius in CANVAS-internal pixels, not screen pixels — the canvas
    // can be several thousand px wide while displayed at a few hundred, so
    // a fixed screen-pixel radius would make the target area far too small
    // (or too large) depending on the photo's resolution.
    const grabRadius = () => canvas.width / 50;

    canvas.addEventListener('pointerdown', (e) => {
      const originalVertices = currentVerticesRef.current;
      if (!originalVertices) return;
      const p = canvasPointFromEvent(e);
      const r = grabRadius();

      // First: is this close to an EXISTING handle's current (dragged)
      // position? Grabbing it again should move it further, not create a
      // second handle on top of it.
      let bestExistingIdx = -1;
      let bestExistingDist = Infinity;
      sculptHandlesRef.current.forEach((h, idx) => {
        const d = Math.hypot(p.x - h.targetX, p.y - h.targetY);
        if (d < r && d < bestExistingDist) {
          bestExistingDist = d;
          bestExistingIdx = idx;
        }
      });
      if (bestExistingIdx !== -1) {
        sculptActiveHandleRef.current = bestExistingIdx;
        canvas.setPointerCapture(e.pointerId);
        return;
      }

      // Otherwise: find the nearest ORIGINAL landmark position among only
      // the currently ENABLED regions' points — this is what "tick a
      // region to make it draggable" actually means; a point whose region
      // checkbox is off isn't a valid grab target even if it's the
      // physically closest one.
      let bestVertexIdx = -1;
      let bestVertexDist = Infinity;
      for (const i of enabledVertexIndices()) {
        const vx = originalVertices[i * 2] ?? 0;
        const vy = originalVertices[i * 2 + 1] ?? 0;
        const d = Math.hypot(p.x - vx, p.y - vy);
        if (d < r && d < bestVertexDist) {
          bestVertexDist = d;
          bestVertexIdx = i;
        }
      }
      if (bestVertexIdx === -1) return; // clicked empty space, or an ungrabbable point — ignore

      sculptHandlesRef.current.push({
        vertexIndex: bestVertexIdx,
        targetX: originalVertices[bestVertexIdx * 2] ?? 0,
        targetY: originalVertices[bestVertexIdx * 2 + 1] ?? 0,
      });
      sculptActiveHandleRef.current = sculptHandlesRef.current.length - 1;
      canvas.setPointerCapture(e.pointerId);
    });

    canvas.addEventListener('pointermove', (e) => {
      const activeIdx = sculptActiveHandleRef.current;
      if (activeIdx === null) return;
      const handle = sculptHandlesRef.current[activeIdx];
      if (!handle) return;
      const p = canvasPointFromEvent(e);
      handle.targetX = p.x;
      handle.targetY = p.y;
      sculptRender();
    });

    const endDrag = (e: PointerEvent) => {
      if (sculptActiveHandleRef.current === null) return;
      sculptActiveHandleRef.current = null;
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        // no-op — pointer capture may already be gone (e.g. pointercancel)
      }
    };
    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);
  }

  function sculptRender() {
    const originalVertices = currentVerticesRef.current;
    const mesh = currentMeshRef.current;
    const pixiApp = pixiAppRef.current;
    const image = lastImageRef.current;
    const meshCanvas = meshCanvasRef.current;
    const sculptCanvas = sculptCanvasRef.current;
    if (!originalVertices || !mesh || !pixiApp || !image || !meshCanvas || !sculptCanvas) return;

    const sigma = sculptSigmaRatioRef.current * sculptInterOcularRef.current;
    const controlPoints = sculptHandlesRef.current.map((h) => {
      const ox = originalVertices[h.vertexIndex * 2] ?? 0;
      const oy = originalVertices[h.vertexIndex * 2 + 1] ?? 0;
      return { x: ox, y: oy, dx: h.targetX - ox, dy: h.targetY - oy };
    });

    const warped = new Float32Array(originalVertices.length);
    for (let i = 0; i < originalVertices.length; i += 2) {
      const x = originalVertices[i] ?? 0;
      const y = originalVertices[i + 1] ?? 0;
      let dx = 0;
      let dy = 0;
      if (sigma > 0) {
        for (const cp of controlPoints) {
          const d2 = (x - cp.x) ** 2 + (y - cp.y) ** 2;
          const weight = Math.exp(-d2 / (2 * sigma * sigma));
          dx += weight * cp.dx;
          dy += weight * cp.dy;
        }
      }
      warped[i] = x + dx;
      warped[i + 1] = y + dy;
    }

    // Live triangle-fold count — the same check Smile/Sad run once at the
    // end, run continuously here so dragging too far gives instant feedback
    // instead of a surprise after the fact.
    let invertedTriangles = 0;
    for (let i = 0; i < FACEMESH_TESSELATION_FLAT.length; i += 3) {
      const a = FACEMESH_TESSELATION_FLAT[i] ?? 0;
      const b = FACEMESH_TESSELATION_FLAT[i + 1] ?? 0;
      const c = FACEMESH_TESSELATION_FLAT[i + 2] ?? 0;
      const ax = originalVertices[a * 2] ?? 0;
      const ay = originalVertices[a * 2 + 1] ?? 0;
      const bx = originalVertices[b * 2] ?? 0;
      const by = originalVertices[b * 2 + 1] ?? 0;
      const cx = originalVertices[c * 2] ?? 0;
      const cy = originalVertices[c * 2 + 1] ?? 0;
      const before = (bx - ax) * (cy - ay) - (cx - ax) * (by - ay);
      const wax = warped[a * 2] ?? 0;
      const way = warped[a * 2 + 1] ?? 0;
      const wbx = warped[b * 2] ?? 0;
      const wby = warped[b * 2 + 1] ?? 0;
      const wcx = warped[c * 2] ?? 0;
      const wcy = warped[c * 2 + 1] ?? 0;
      const after = (wbx - wax) * (wcy - way) - (wcx - wax) * (wby - way);
      if (Math.sign(before) !== Math.sign(after)) invertedTriangles++;
    }

    mesh.vertices = warped;
    pixiApp.render();

    const sctx = sculptCanvas.getContext('2d');
    if (!sctx) return;
    sctx.clearRect(0, 0, sculptCanvas.width, sculptCanvas.height);

    // Overlay mode: the reference photo is the BASE layer, stretched to
    // this canvas's own dimensions (its aspect ratio will generally differ
    // from the working photo's — stretch rather than letterbox/crop,
    // simplest to reason about when comparing feature positions
    // proportionally). The working photo + mesh are drawn on top at
    // globalAlpha = referenceOpacityRef.current, so raising that opacity
    // shows more of the working photo, lowering it shows more of the
    // reference underneath.
    if (referenceLayout === 'overlay' && referenceImageRef.current) {
      sctx.globalAlpha = 1;
      sctx.drawImage(referenceImageRef.current, 0, 0, sculptCanvas.width, sculptCanvas.height);
      sctx.globalAlpha = referenceOpacityRef.current;
    }
    sctx.drawImage(image, 0, 0);
    sctx.drawImage(meshCanvas, 0, 0);
    sctx.globalAlpha = 1;

    // Both blocks below are gated on sculptShowMarkersRef — unchecking
    // "Show markers" (default on) draws just the warped photo, nothing
    // else, so the result can be judged cleanly. Handles themselves are NOT
    // cleared by this — only what gets drawn — so re-checking the box, or
    // dragging again, picks up exactly where it left off.
    if (sculptShowMarkersRef.current) {
      // Draw every currently-enabled, not-yet-grabbed point as a small dot
      // — this is the actual fix for "I don't see what I can drag":
      // previously nothing showed until you'd already dragged it. Drawn
      // small and semi-transparent, colored by region, so a densely
      // populated region (e.g. Cheeks, ~250+ points) doesn't turn into a
      // solid mass over the photo.
      const alreadyHandled = new Set(sculptHandlesRef.current.map((h) => h.vertexIndex));
      // Base divisor matches drawRegionColoredMesh()'s "other" dot size
      // (canvas.width / 500) exactly — a real mismatch found live: this
      // used to be /700 (a smaller dot as a fraction of its own canvas), so
      // even at the same on-screen display size, this canvas's
      // available-point dots looked noticeably smaller than the reference
      // canvas's dots right next to them, for no intended reason.
      const availableRadius =
        Math.max(sculptCanvas.width / 500, 3) * sculptMarkerSizeRatioRef.current;
      for (const name of sculptEnabledCategoriesRef.current) {
        const set = sculptCategoryIndicesRef.current[name];
        if (!set) continue;
        const color = SCULPT_CATEGORY_COLORS[name] ?? '#ffffff';
        for (const idx of set) {
          if (alreadyHandled.has(idx)) continue; // already has its own bigger, brighter handle marker
          const vx = originalVertices[idx * 2] ?? 0;
          const vy = originalVertices[idx * 2 + 1] ?? 0;
          sctx.beginPath();
          sctx.arc(vx, vy, availableRadius, 0, 2 * Math.PI);
          sctx.fillStyle = color;
          sctx.globalAlpha = 0.75;
          sctx.fill();
          sctx.globalAlpha = 1;
        }
      }

      // Draw handle markers on top — a small dot at the ORIGINAL position
      // (where it was grabbed from) connected by a line to the current
      // (dragged) position, so it's visually clear both where a point came
      // from and how far it's moved.
      const handleRadius = Math.max(sculptCanvas.width / 300, 5) * sculptMarkerSizeRatioRef.current;
      for (const h of sculptHandlesRef.current) {
        const ox = originalVertices[h.vertexIndex * 2] ?? 0;
        const oy = originalVertices[h.vertexIndex * 2 + 1] ?? 0;
        sctx.beginPath();
        sctx.moveTo(ox, oy);
        sctx.lineTo(h.targetX, h.targetY);
        sctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
        sctx.lineWidth = Math.max(sculptCanvas.width / 1000, 2);
        sctx.stroke();

        sctx.beginPath();
        sctx.arc(ox, oy, handleRadius * 0.5, 0, 2 * Math.PI);
        sctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
        sctx.fill();

        sctx.beginPath();
        sctx.arc(h.targetX, h.targetY, handleRadius, 0, 2 * Math.PI);
        sctx.fillStyle = '#e91e63';
        sctx.fill();
        sctx.lineWidth = Math.max(handleRadius / 4, 1.5);
        sctx.strokeStyle = 'rgba(0, 0, 0, 0.65)';
        sctx.stroke();
      }
    }

    renderSculptHandleList(invertedTriangles);
  }

  function renderSculptHandleList(invertedTriangles: number) {
    const el = sculptHandlesElRef.current;
    const originalVertices = currentVerticesRef.current;
    if (!el || !originalVertices) return;

    if (sculptHandlesRef.current.length === 0) {
      el.textContent = 'No handles yet — drag a point on the face.';
      return;
    }
    el.innerHTML = '';
    const summary = document.createElement('div');
    summary.textContent =
      `${sculptHandlesRef.current.length} handle(s). Falloff radius: ${(sculptSigmaRatioRef.current * sculptInterOcularRef.current).toFixed(1)}px. ` +
      (invertedTriangles === 0
        ? 'Triangles folded: 0 ✅'
        : `Triangles folded: ${invertedTriangles} ⚠️ (drag less far, or increase the falloff radius)`);
    el.appendChild(summary);

    sculptHandlesRef.current.forEach((h, idx) => {
      const ox = originalVertices[h.vertexIndex * 2] ?? 0;
      const oy = originalVertices[h.vertexIndex * 2 + 1] ?? 0;
      const dx = h.targetX - ox;
      const dy = h.targetY - oy;
      const row = document.createElement('div');
      row.textContent = `#${h.vertexIndex}: dx=${dx.toFixed(1)}px, dy=${dy.toFixed(1)}px `;
      const removeBtn = document.createElement('button');
      removeBtn.textContent = '× remove';
      removeBtn.type = 'button';
      removeBtn.addEventListener('click', () => {
        sculptHandlesRef.current.splice(idx, 1);
        sculptRender();
      });
      row.appendChild(removeBtn);
      el.appendChild(row);
    });
  }

  function downloadSculptParameters() {
    const originalVertices = currentVerticesRef.current;
    if (!originalVertices) return;
    const payload = {
      interOcular: sculptInterOcularRef.current,
      sigmaRatio: sculptSigmaRatioRef.current,
      sigmaPx: sculptSigmaRatioRef.current * sculptInterOcularRef.current,
      handles: sculptHandlesRef.current.map((h) => {
        const ox = originalVertices[h.vertexIndex * 2] ?? 0;
        const oy = originalVertices[h.vertexIndex * 2 + 1] ?? 0;
        return {
          vertexIndex: h.vertexIndex,
          dxPx: h.targetX - ox,
          dyPx: h.targetY - oy,
          // Also expressed relative to inter-ocular distance, the same unit
          // Smile/Sad's own magnitudes are defined in — this is what you'd
          // actually hand back to hardcode a new expression.
          dxRatio: (h.targetX - ox) / sculptInterOcularRef.current,
          dyRatio: (h.targetY - oy) / sculptInterOcularRef.current,
        };
      }),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'sculpt-parameters.json';
    a.click();
  }

  // --- Phase 5d: match reference ------------------------------------------
  // Ported from exploring/face-landmarks/index.html — see that folder's
  // README and FACE_FILTERS_ROADMAP.md §10 for the full account, including
  // why this is a legally clean alternative to reverse-engineering a
  // Snapchat/Instagram/TikTok filter's proprietary file (measure a public
  // visual effect with our own pipeline, don't decompile someone else's).
  //
  // Unlike the interactive sculpt tool above (a handful of manually dragged
  // control points, smoothly interpolated across the rest of the mesh via a
  // Gaussian falloff, because most of the mesh has no direct target), this
  // has REAL 1:1 correspondence for all 468 points — the reference photo's
  // own detection gives an actual target for every single vertex, not just
  // a few. So this does NOT reuse the handle/falloff system at all: mixing
  // "468 full-strength pull points, each also leaking a Gaussian pull onto
  // its neighbors" would compound and overshoot, since neighboring points
  // already have their OWN full-strength target nearby. Every vertex here
  // gets a DIRECT, independent target — no interpolation needed or wanted.
  //
  // The mapping, per vertex: take the reference landmark's pixel position,
  // recenter it on the reference face's own eye-midpoint, and rescale by
  // the reference face's own inter-ocular distance — a scale- and
  // position-INVARIANT representation of "where this point sits, relative
  // to this face's own proportions" (the same normalization Smile/Sad's
  // magnitudes already use, just applied to full 2D position instead of a
  // displacement). Re-project that into the WORKING photo's own
  // eye-midpoint + inter-ocular scale, and that's the target on the working
  // face.
  function matchReference() {
    const originalVertices = currentVerticesRef.current;
    const mesh = currentMeshRef.current;
    const pixiApp = pixiAppRef.current;
    const image = lastImageRef.current;
    const meshCanvas = meshCanvasRef.current;
    const matchCanvas = matchCanvasRef.current;
    const referenceImage = referenceImageRef.current;
    const referenceResult = referenceResultRef.current;
    if (
      !originalVertices ||
      !mesh ||
      !pixiApp ||
      !image ||
      !meshCanvas ||
      !matchCanvas ||
      !referenceImage ||
      !referenceResult ||
      !lastResult ||
      load.kind !== 'ready'
    ) {
      return;
    }
    const workingLandmarks = lastResult.faceLandmarks[0];
    const refLandmarks = referenceResult.faceLandmarks[0];
    if (!workingLandmarks || !refLandmarks) {
      setMatchStatus('No face detected in one of the two photos — nothing to match.');
      return;
    }

    const refFrame = faceFrame(
      refLandmarks,
      referenceImage.naturalWidth,
      referenceImage.naturalHeight,
    );
    const workFrame = faceFrame(workingLandmarks, image.naturalWidth, image.naturalHeight);
    const strength = matchStrengthRef.current;

    const warped = new Float32Array(originalVertices.length);
    let totalDisplacement = 0;
    for (let i = 0; i < 468; i++) {
      const ox = originalVertices[i * 2] ?? 0;
      const oy = originalVertices[i * 2 + 1] ?? 0;
      const refPoint = refLandmarks[i];
      if (!refPoint) continue;
      const refPxX = refPoint.x * referenceImage.naturalWidth;
      const refPxY = refPoint.y * referenceImage.naturalHeight;
      const relX = (refPxX - refFrame.cx) / refFrame.interOcular;
      const relY = (refPxY - refFrame.cy) / refFrame.interOcular;
      const targetX = workFrame.cx + relX * workFrame.interOcular;
      const targetY = workFrame.cy + relY * workFrame.interOcular;
      const dx = (targetX - ox) * strength;
      const dy = (targetY - oy) * strength;
      warped[i * 2] = ox + dx;
      warped[i * 2 + 1] = oy + dy;
      totalDisplacement += Math.hypot(dx, dy);
    }
    const avgDisplacement = totalDisplacement / 468;

    // Same triangle-fold check used everywhere else on this screen — a
    // direct per-vertex remap can still fold a triangle if two neighboring
    // points end up swapping relative order.
    const signedArea = (verts: Float32Array, ia: number, ib: number, ic: number) => {
      const ax = verts[ia * 2] ?? 0;
      const ay = verts[ia * 2 + 1] ?? 0;
      const bx = verts[ib * 2] ?? 0;
      const by = verts[ib * 2 + 1] ?? 0;
      const cx = verts[ic * 2] ?? 0;
      const cy = verts[ic * 2 + 1] ?? 0;
      return (bx - ax) * (cy - ay) - (cx - ax) * (by - ay);
    };
    let invertedTriangles = 0;
    for (let i = 0; i < FACEMESH_TESSELATION_FLAT.length; i += 3) {
      const a = FACEMESH_TESSELATION_FLAT[i] ?? 0;
      const b = FACEMESH_TESSELATION_FLAT[i + 1] ?? 0;
      const c = FACEMESH_TESSELATION_FLAT[i + 2] ?? 0;
      const before = signedArea(originalVertices, a, b, c);
      const after = signedArea(warped, a, b, c);
      if (Math.sign(before) !== Math.sign(after)) invertedTriangles++;
    }

    mesh.vertices = warped;
    pixiApp.render();

    matchCanvas.width = image.naturalWidth;
    matchCanvas.height = image.naturalHeight;
    const mctx = matchCanvas.getContext('2d');
    if (!mctx) return;
    mctx.clearRect(0, 0, matchCanvas.width, matchCanvas.height);
    mctx.drawImage(image, 0, 0);
    mctx.drawImage(meshCanvas, 0, 0);

    // Re-detect on the result — the same "does MediaPipe itself agree"
    // check Smile/Sad use, here confirming the warp didn't distort the face
    // past recognizability rather than measuring a specific expression.
    const afterResult = load.landmarker.detect(matchCanvas);
    const faceStillDetected = afterResult.faceLandmarks.length > 0;

    setMatchStatus(
      [
        `Matched all 468 points at strength ${strength.toFixed(2)}. Average displacement: ${avgDisplacement.toFixed(1)}px (scaled from a ${workFrame.interOcular.toFixed(1)}px working inter-ocular distance).`,
        `Triangle check: ${invertedTriangles} of ${FACEMESH_TESSELATION_FLAT.length / 3} triangles inverted (folded over themselves).`,
        `Face still detected after warping: ${faceStillDetected ? 'yes' : 'NO — warp broke detection, try a lower strength'}.`,
        invertedTriangles === 0 && faceStillDetected
          ? '✅ Applied cleanly — inspect the image below to judge how close it reads to the reference.'
          : '⚠️ Inspect visually — one or more checks did not clearly pass. Try a lower strength.',
      ].join('\n'),
    );

    // Sculpting after a match continues from the ORIGINAL vertices, not
    // this matched result — see currentVerticesRef's own doc comment.
    // Re-render the sculpt canvas so it doesn't silently show a stale
    // pre-match frame next to this fresh result.
    sculptRender();
  }

  // --- Phase 6: Snap Camera Kit on a still photo --------------------------
  // The question this exists to answer, and nothing more: does a real Snap
  // Lens applied to an UPLOADED STILL PHOTO look good enough to be worth
  // adopting Camera Kit over the hand-built warp pipeline above?
  //
  // Why that's genuinely in doubt, and not answerable from the docs: Camera
  // Kit is built for the live selfie camera, and community/marketplace
  // Lenses are authored against a moving face — many are animated, reactive,
  // or driven by triggers that assume a video stream over time. Snap's docs
  // confirm an `<img>` can be a source (`createImageSource`) and that
  // `session.output.live` is a canvas, but say nothing about how a
  // camera-authored Lens actually behaves on one frozen frame. That is the
  // risk to retire BEFORE paying a creator to build a custom expression set.
  //
  // Deliberately NOT wired into the avatar upload path, and not deployed —
  // this is a throwaway probe behind the tester gate, same as everything
  // else on this screen.
  //
  // The SDK is imported dynamically, never at module scope: it pulls a
  // WebAssembly runtime, and no other route should carry that weight (the
  // same reasoning as MediaPipe's lazy load at the top of this file).
  // Fetches every Lens in the configured group so the picker can be filled
  // from the account rather than from hand-copied Lens IDs.
  //
  // `loadLensGroups` resolves even when some groups fail — it reports those
  // in an `errors` array instead of rejecting — so an empty result is a
  // normal outcome to surface, not an exception to catch.
  async function loadCameraKitLenses() {
    const apiToken = cameraKitToken.trim();
    const groupId = cameraKitGroupId.trim();
    if (!apiToken || !groupId) {
      setCameraKitStatus('An API token and a Lens Group ID are both needed to list Lenses.');
      return;
    }
    setCameraKitBusy(true);
    setCameraKitStatus('Loading the Camera Kit runtime and listing Lenses in the group…');
    try {
      const { bootstrapCameraKit } = await import('@snap/camera-kit');
      const cameraKit = await bootstrapCameraKit({ apiToken });
      const { lenses, errors } = await cameraKit.lensRepository.loadLensGroups([groupId]);
      setCameraKitLenses(lenses.map((lens) => ({ id: lens.id, name: lens.name })));
      // Default to a FACE lens, not simply the first in the group.
      //
      // Snap's demo group is a tour of SDK features, not a filter pack: its
      // first entry is "Simple Typing Test", which paints a full-screen
      // white keyboard UI over the photo. Defaulting to index 0 therefore
      // produced "Camera Kit covers the picture in a white square", which
      // reads as a broken integration rather than as "that lens is a
      // typing demo". Preferring a face-related name makes the default
      // relevant to what this experiment is actually asking.
      //
      // The same trap exists in our OWN group: it still holds "Felipe Test",
      // the throwaway Lens used to prove the submission pipeline worked, and
      // that name sorts ahead of anything useful. Hence the expression terms
      // below — they are what our real filters are named after.
      const faceLens = lenses.find((lens) =>
        /face|expression|distort|hair|sad|cry|tear/i.test(lens.name),
      );
      setCameraKitSelectedLensId((current) => current || (faceLens ?? lenses[0])?.id || '');
      setCameraKitStatus(
        [
          `Found ${lenses.length} Lens(es) in group ${groupId}.`,
          errors.length > 0
            ? `⚠️ ${errors.length} error(s) while querying: ${errors.map((e) => e.message).join('; ')}`
            : '',
          lenses.length > 0
            ? 'Pick one below, then apply it to the photo.'
            : '⚠️ The group is empty — nothing to apply. Check the Group ID, or add Lenses to it in the portal.',
        ]
          .filter(Boolean)
          .join('\n'),
      );
    } catch (error) {
      setCameraKitStatus(
        `❌ ${error instanceof Error ? error.message : String(error)}\n\n` +
          'Common causes: the API token is for the wrong environment (a production token ' +
          'only works once Snap has approved the app), or the Group ID does not belong to ' +
          'this token’s organization.',
      );
    } finally {
      setCameraKitBusy(false);
    }
  }

  async function runCameraKit() {
    const image = lastImageRef.current;
    const canvas = cameraKitCanvasRef.current;
    if (!image || !canvas) {
      setCameraKitStatus('Upload a photo first.');
      return;
    }
    const apiToken = cameraKitToken.trim();
    const lensId = cameraKitSelectedLensId.trim();
    const groupId = cameraKitGroupId.trim();
    if (!apiToken || !groupId) {
      setCameraKitStatus('Fill in the API token and Lens Group ID first.');
      return;
    }
    if (!lensId) {
      setCameraKitStatus('Load the Lenses in the group first, then pick one.');
      return;
    }

    setCameraKitBusy(true);
    setCameraKitStatus('Loading the Camera Kit runtime (WebAssembly, first run is slower)…');

    try {
      const { bootstrapCameraKit, createImageSource } = await import('@snap/camera-kit');
      const cameraKit = await bootstrapCameraKit({ apiToken });

      setCameraKitStatus('Runtime ready. Creating a session and loading the Lens…');

      // Tear down the PREVIOUS run's session, not this one — see the note
      // where the session is deliberately left alive below.
      const previous = cameraKitSessionRef.current;
      cameraKitSessionRef.current = null;
      if (previous) {
        previous.pause();
        void previous.destroy();
      }

      const session = await cameraKit.createSession({ liveRenderTarget: canvas });
      cameraKitSessionRef.current = session;

      {
        // The still photo IS the source — no getUserMedia, no camera
        // permission prompt. This is the whole point of the probe.
        const source = createImageSource(image);
        await session.setSource(source);

        const lens = await cameraKit.lensRepository.loadLens(lensId, groupId);
        await session.applyLens(lens);
        await session.play();

        // A Lens is a real-time effect: the first rendered frame is not
        // necessarily its finished state (tracking settles, animations and
        // deformations ease in). So don't grab frame one — let it run
        // briefly, then read the canvas. If a Lens still looks half-applied
        // after this, that IS the finding, not a bug to hide.
        await new Promise((resolve) => setTimeout(resolve, 1500));

        const rendered = canvas.width > 0 && canvas.height > 0;
        // Was anything actually painted? A silently-empty canvas is the
        // failure mode worth catching explicitly here, since it looks
        // identical to "still loading" on screen.
        // Count DISTINCT colours, not just non-zero alpha. The first version
        // of this check tested alpha only, and a fully opaque blank canvas
        // sailed through it — reporting success while the screen showed
        // nothing. Distinct colours is the property actually being claimed.
        let distinctColours = 0;
        if (rendered) {
          const probe = document.createElement('canvas');
          probe.width = canvas.width;
          probe.height = canvas.height;
          const pctx = probe.getContext('2d');
          if (pctx) {
            pctx.drawImage(canvas, 0, 0);
            const { data } = pctx.getImageData(0, 0, probe.width, probe.height);
            const seen = new Set<number>();
            for (let i = 0; i < data.length && seen.size <= 24; i += 4 * 499) {
              seen.add((data[i]! << 16) | (data[i + 1]! << 8) | data[i + 2]!);
            }
            distinctColours = seen.size;
          }
        }
        const painted = distinctColours > 4;

        setCameraKitStatus(
          [
            `Lens applied to the still photo. Output canvas: ${canvas.width}x${canvas.height}, ${distinctColours}+ distinct colours sampled.`,
            painted
              ? '✅ Rendered real image content — judge it visually below. The Lens is still running (a Lens is a live effect), so anything animated will keep moving.'
              : '⚠️ The output looks blank or near-uniform. That usually means the Lens loaded but rendered nothing meaningful against a static source — a real answer to this experiment, not necessarily a bug in this page.',
          ].join('\n'),
        );
      }
      // NOTE: the session is deliberately NOT destroyed here.
      //
      // It was, in the first version, in a `finally` — and that silently
      // destroyed the very thing this experiment exists to look at.
      // `destroy()` tears down Camera Kit's WebGL context, which blanks the
      // on-screen canvas, while `getImageData` still returns the backing
      // store. The result: a canvas that reported real pixels in code and
      // showed pure white to the eye. Cleanup now happens at the START of
      // the next run (above) and on unmount, so exactly one session is ever
      // alive and the current result stays visible.
    } catch (error) {
      setCameraKitStatus(
        `❌ ${error instanceof Error ? error.message : String(error)}\n\n` +
          'Common causes: the API token is for the wrong environment (staging vs production), ' +
          'the Lens ID / Group ID pair does not exist under that token’s organization, or the ' +
          'Lens is not published to a Lens Folder that this token can read.',
      );
    } finally {
      setCameraKitBusy(false);
    }
  }

  function downloadJson() {
    if (!lastResult) return;
    const blob = new Blob([JSON.stringify(lastResult, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'face-landmarks.json';
    a.click();
  }

  return (
    <main className="screen">
      <h1>🧪 Face landmarks</h1>
      <p className="muted">
        Upload a photo, get the 478 face landmarks drawn over it. Internal test tool — see{' '}
        <code>exploring/face-landmarks/README.md</code> and <code>FACE_FILTERS_ROADMAP.md</code> in
        the repo for what this is and isn't.
      </p>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        Runs entirely in your browser. The photo is never sent to 2podium's backend — only
        MediaPipe's own CDN, once, for the runtime and model file.
      </p>

      <div
        className={`${styles.dropZone} ${dragging ? styles.drag : ''}`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const file = e.dataTransfer.files[0];
          if (file) void handleFile(file);
        }}
      >
        {load.kind === 'ready' ? 'Click to choose a photo, or drag one here.' : status}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
      </div>

      <div className={styles.canvasWrap}>
        <canvas ref={canvasRef} />
      </div>

      <div className={styles.legend}>
        {FACE_REGIONS.map((region) => (
          <span key={region.label} className={styles.legendItem}>
            <span className={styles.legendSwatch} style={{ background: region.color }} />
            {region.label}
          </span>
        ))}
        <span className={styles.legendItem}>
          <span className={styles.legendSwatch} style={{ background: OTHER_MESH_COLOR }} />
          {OTHER_MESH_LABEL}
        </span>
      </div>

      <p className={styles.status}>{status}</p>

      {lastResult ? (
        <button type="button" className="button" onClick={downloadJson}>
          Download raw landmarks as JSON
        </button>
      ) : null}

      <hr className={styles.divider} />

      <h2>Phase 3 — render the photo as a mesh</h2>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        Renders the same photo as a textured triangle mesh, every vertex left at its original
        position — no warping. Checks the roadmap's own bar for this phase: the mesh render must be
        pixel-identical to the source photo.
      </p>

      <button
        type="button"
        className="button"
        disabled={!lastResult || lastResult.faceLandmarks.length === 0 || meshBusy}
        onClick={() => void renderMesh()}
      >
        {lastResult && lastResult.faceLandmarks.length > 0
          ? 'Render mesh (Phase 3)'
          : 'Detect a face first'}
      </button>

      <div className={styles.canvasWrap}>
        <canvas ref={meshCanvasRef} />
      </div>

      {meshStatus ? <p className={styles.status}>{meshStatus}</p> : null}

      <hr className={styles.divider} />

      <h2>Phase 4 — first warp: smile</h2>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        Displaces the mouth-corner vertices upward and slightly outward, with a smooth falloff so
        nearby vertices follow along and everything far from the mouth stays put — no hard-edged
        tear. Composited back onto the original photo so it reads as an actual warped photo. The
        roadmap's own bar for this phase: reads as a smile, not a smear.
      </p>

      <button
        type="button"
        className="button"
        disabled={!meshReady || smileBusy}
        onClick={() => void renderSmile()}
      >
        {meshReady ? '😊 Smile (Phase 4)' : 'Render mesh first'}
      </button>

      <div className={styles.canvasWrap}>
        <canvas ref={smileCanvasRef} />
      </div>

      {smileStatus ? <p className={styles.status}>{smileStatus}</p> : null}

      <hr className={styles.divider} />

      <h2>Phase 5 — expression authoring: sad</h2>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        Mouth corners pulled down and slightly in (AU15), inner eyebrows raised and drawn slightly
        together (AU1+AU4 — the roadmap calls this "the single most sad-reading deformation that
        exists"), a subtle downward droop at the outer eye corners. No tears, no overlay — just the
        geometric warp, tuned to read as sad/worried.
      </p>

      <button
        type="button"
        className="button"
        disabled={!meshReady || sadBusy}
        onClick={() => void renderSad()}
      >
        {meshReady ? '😢 Sad (Phase 5)' : 'Render mesh first'}
      </button>

      <div className={styles.canvasWrap}>
        <canvas ref={sadCanvasRef} />
      </div>

      {sadStatus ? <p className={styles.status}>{sadStatus}</p> : null}

      <hr className={styles.divider} />

      <h2>Phase 5b — interactive sculpt</h2>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        Tick a region below to reveal its draggable points, then click and drag any of them to
        reshape the face by hand — same warp math as Smile and Sad above (a Gaussian falloff around
        wherever you grab), but the target position is wherever you drag to, not a hardcoded number.
        The list below updates live with exactly what's been moved, and can be exported as JSON.
        Handles that are close together pull on each other a little — that's expected, not a bug.
        Zoom in for finer control over small movements.
      </p>

      {/* Deliberately no JSX children — see the sculptHandlesElRef comment
          below for why. Each checkbox's own change handler updates
          sculptEnabledCategoriesRef and re-renders imperatively. */}
      <div className={styles.sculptRegionToggles} ref={sculptRegionTogglesElRef} />

      <p className="muted" style={{ fontSize: '0.85rem' }}>
        Falloff radius:{' '}
        <input
          ref={sculptSigmaSliderRef}
          type="range"
          min="0.08"
          max="0.6"
          step="0.01"
          defaultValue="0.22"
          onChange={(e) => {
            sculptSigmaRatioRef.current = Number(e.target.value);
            if (sculptSigmaValueElRef.current) {
              sculptSigmaValueElRef.current.textContent = sculptSigmaRatioRef.current.toFixed(2);
            }
            sculptRender();
          }}
        />{' '}
        <span ref={sculptSigmaValueElRef}>0.22</span>x inter-ocular distance
      </p>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        Zoom:{' '}
        <input
          type="range"
          min="1"
          max="4"
          step="0.1"
          defaultValue="1"
          onChange={(e) => {
            const zoom = Number(e.target.value);
            if (sculptZoomValueElRef.current) {
              sculptZoomValueElRef.current.textContent = zoom.toFixed(1);
            }
            const canvas = sculptCanvasRef.current;
            if (canvas && sculptBaseWidthPxRef.current > 0) {
              // maxWidth override required — the canvasWrap CSS's
              // "max-width: 100%" (there for the un-zoomed, fit-to-
              // container case) would otherwise clamp this back down
              // regardless of the explicit width. An absolute pixel width,
              // not a percentage — see initSculpt()'s comment on
              // sculptBaseWidthPxRef for why a percentage doesn't work in
              // this component's flex layout.
              canvas.style.maxWidth = 'none';
              canvas.style.width = `${sculptBaseWidthPxRef.current * zoom}px`;
            }
          }}
        />{' '}
        <span ref={sculptZoomValueElRef}>1.0</span>x — scroll the photo below to reach the part you
        zoomed into.
      </p>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        <label>
          <input
            type="checkbox"
            defaultChecked
            onChange={(e) => {
              sculptShowMarkersRef.current = e.target.checked;
              sculptRender();
            }}
          />{' '}
          Show markers (uncheck to preview the result cleanly — handles stay in place, they just
          stop drawing)
        </label>
      </p>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        Marker size:{' '}
        <input
          type="range"
          min="0.1"
          max="2.5"
          step="0.05"
          defaultValue="1"
          onChange={(e) => {
            sculptMarkerSizeRatioRef.current = Number(e.target.value);
            if (sculptMarkerSizeValueElRef.current) {
              sculptMarkerSizeValueElRef.current.textContent =
                sculptMarkerSizeRatioRef.current.toFixed(2);
            }
            sculptRender();
          }}
        />{' '}
        <span ref={sculptMarkerSizeValueElRef}>1.0</span>x — shrink so a marker doesn't block your
        view of what's underneath it while you drag.
      </p>

      <hr className={styles.divider} />

      <h2>Phase 5c — reference image</h2>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        Upload a second photo — e.g. a screenshot of a filter you're trying to reproduce — to
        compare against while sculpting. It gets its own landmark mesh too, so you can read off
        roughly where a feature landed instead of eyeballing it. This is a measurement aid, not a
        shortcut to someone else's filter file — see FACE_FILTERS_ROADMAP.md's note on
        reverse-engineering by observation, not extraction.
      </p>

      <div
        className={`${styles.dropZone} ${referenceDragging ? styles.drag : ''}`}
        onClick={() => referenceFileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setReferenceDragging(true);
        }}
        onDragLeave={() => setReferenceDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setReferenceDragging(false);
          const file = e.dataTransfer.files[0];
          if (file) void handleReferenceFile(file);
        }}
      >
        Click to choose a reference photo, or drag one here.
        <input
          ref={referenceFileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleReferenceFile(file);
          }}
        />
      </div>

      {referenceReady ? (
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          <label>
            <input
              type="radio"
              name="referenceLayout"
              checked={referenceLayout === 'side-by-side'}
              onChange={() => {
                setReferenceLayout('side-by-side');
                sculptRender();
              }}
            />{' '}
            Side by side
          </label>{' '}
          <label>
            <input
              type="radio"
              name="referenceLayout"
              checked={referenceLayout === 'overlay'}
              onChange={() => {
                setReferenceLayout('overlay');
                sculptRender();
              }}
            />{' '}
            Overlay
          </label>{' '}
          — in overlay mode, the reference sits UNDER your working photo; the slider below controls
          how much of your working photo shows through on top of it.
        </p>
      ) : null}
      {referenceReady && referenceLayout === 'overlay' ? (
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          Working photo opacity (overlay mode):{' '}
          <input
            type="range"
            min="0"
            max="1"
            step="0.02"
            defaultValue="0.6"
            onChange={(e) => {
              referenceOpacityRef.current = Number(e.target.value);
              if (referenceOpacityValueElRef.current) {
                referenceOpacityValueElRef.current.textContent =
                  referenceOpacityRef.current.toFixed(2);
              }
              sculptRender();
            }}
          />{' '}
          <span ref={referenceOpacityValueElRef}>0.60</span>
        </p>
      ) : null}

      <button
        type="button"
        className="button"
        disabled={!sculptReady}
        onClick={resetSculpt}
        style={{ marginRight: 'var(--space-2)' }}
      >
        {sculptReady ? 'Reset sculpt' : 'Render mesh first'}
      </button>
      {sculptReady ? (
        <button type="button" className="button" onClick={downloadSculptParameters}>
          Download parameters as JSON
        </button>
      ) : null}

      <div className={styles.comparisonRow}>
        {/* Always mounted once a reference exists, never conditionally
            removed — toggling layout only needs to hide/show this, not
            recreate it. Unmounting and remounting the canvas would lose
            everything drawRegionColoredMesh() already painted onto it,
            since that's imperative canvas state, not React state. */}
        <div
          className={styles.canvasWrap}
          style={{
            display: referenceReady && referenceLayout === 'side-by-side' ? 'block' : 'none',
          }}
        >
          <canvas ref={referenceCanvasRef} />
        </div>
        <div className={styles.canvasWrap}>
          <canvas ref={sculptCanvasRef} className={styles.sculptCanvas} />
        </div>
      </div>

      {/* Deliberately no JSX children — this element's contents are set
          imperatively in renderSculptHandleList(), same reasoning as the
          canvases above it: it updates on every pointermove during a drag,
          far too often to route through React state/re-renders. Giving it
          children here would fight React's reconciler the first time some
          unrelated state change causes a re-render (React would blow away
          the imperatively-inserted DOM to match a JSX snapshot it no
          longer matches). */}
      <p className={styles.status} ref={sculptHandlesElRef} />

      <hr className={styles.divider} />

      <h2>Phase 5d — match reference</h2>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        Automatically moves every one of the 468 mesh points on your working photo to the same{' '}
        <em>relative</em> position they're at on the reference photo — not the same raw pixel
        coordinates (the two photos are different sizes and the faces sit in different places), but
        the same position measured relative to that face's own eye-to-eye distance, then re-scaled
        onto your working face's own proportions. This is the measurement-based approach from
        FACE_FILTERS_ROADMAP.md §10: reproduce what a reference expression actually does, point by
        point, real correspondence — not a guess.
      </p>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        Match strength:{' '}
        <input
          type="range"
          min="0"
          max="1"
          step="0.02"
          defaultValue="1"
          onChange={(e) => {
            matchStrengthRef.current = Number(e.target.value);
            if (matchStrengthValueElRef.current) {
              matchStrengthValueElRef.current.textContent = matchStrengthRef.current.toFixed(2);
            }
          }}
        />{' '}
        <span ref={matchStrengthValueElRef}>1.00</span> — 1.0 fully matches the reference's shape;
        lower it to blend partway if a full match looks distorted (very different face proportions
        between the two photos will do that).
      </p>

      <button
        type="button"
        className="button"
        disabled={!sculptReady || !referenceReady}
        onClick={matchReference}
      >
        {sculptReady && referenceReady
          ? 'Match reference'
          : 'Render mesh and load a reference photo first'}
      </button>

      <div className={styles.canvasWrap}>
        <canvas ref={matchCanvasRef} />
      </div>

      {matchStatus ? <p className={styles.status}>{matchStatus}</p> : null}

      <hr className={styles.divider} />

      <h2>Phase 6 — Snap Camera Kit on a still photo</h2>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        Applies a real Snap <em>Lens</em> to the photo uploaded at the top of this page, instead of
        warping the mesh by hand. This is a throwaway probe with one job: find out whether a Lens —
        which Snap builds for the <em>live selfie camera</em> — still looks right on a single frozen
        frame. Snap's docs confirm an <code>&lt;img&gt;</code> can be the input source and that the
        output is a canvas, but say nothing about how a camera-authored Lens behaves on a still
        image. That's the risk worth retiring before commissioning a paid custom Lens.
      </p>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        Credentials are read from the gitignored root <code>.env</code> (
        <code>VITE_CAMERA_KIT_API_TOKEN</code> and <code>VITE_CAMERA_KIT_LENS_GROUP_ID</code> — see{' '}
        <code>.env.example</code> for how to obtain them), and stay editable here so another token
        or group can be tried without restarting the dev server. The Staging token watermarks its
        output and needs no app review; the production one only works once Snap approves the app.
      </p>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        <label>
          API token:{' '}
          <input
            type="password"
            value={cameraKitToken}
            onChange={(e) => setCameraKitToken(e.target.value)}
            placeholder="Staging or production API token"
            style={{ width: '22rem', maxWidth: '100%' }}
          />
        </label>
      </p>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        <label>
          Lens Group ID:{' '}
          <input
            type="text"
            value={cameraKitGroupId}
            onChange={(e) => setCameraKitGroupId(e.target.value)}
            placeholder="e.g. 5f1a2b3c-…"
            style={{ width: '22rem', maxWidth: '100%' }}
          />
        </label>
      </p>

      <p className="muted" style={{ fontSize: '0.85rem' }}>
        {/* Two steps on purpose: listing the group's Lenses is one network
            round trip, and cycling through Lenses afterwards should not
            repeat it. Which Lens survives a still frame varies per Lens, so
            trying several is the actual experiment. */}
        <button type="button" onClick={() => void loadCameraKitLenses()} disabled={cameraKitBusy}>
          1. Load Lenses in this group
        </button>
      </p>

      {cameraKitLenses.length > 0 ? (
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          <label>
            Lens:{' '}
            <select
              value={cameraKitSelectedLensId}
              onChange={(e) => setCameraKitSelectedLensId(e.target.value)}
            >
              {cameraKitLenses.map((lens) => (
                <option key={lens.id} value={lens.id}>
                  {lens.name || lens.id}
                </option>
              ))}
            </select>
          </label>
        </p>
      ) : null}

      <button type="button" onClick={() => void runCameraKit()} disabled={cameraKitBusy}>
        {cameraKitBusy ? 'Working…' : '2. 👻 Apply Snap Lens to the photo'}
      </button>

      <div className={styles.canvasWrap}>
        <canvas ref={cameraKitCanvasRef} />
      </div>

      {cameraKitStatus ? <p className={styles.status}>{cameraKitStatus}</p> : null}
      <ExpressionLab />
      <BrowserExpressionProbe />
      <BrowserPhotoLab />
    </main>
  );
}

// Draws the photo plus region-colored landmark dots onto an arbitrary
// canvas 2D context — extracted from the original single-purpose version so
// it can be reused for the Phase 5c reference-image canvas too, not just
// the main upload canvas.
function drawRegionColoredMesh(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  image: HTMLImageElement,
  result: FaceLandmarkerResult,
) {
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  ctx.drawImage(image, 0, 0);

  // Two passes, not one: a named-feature dot needs to be both bigger AND
  // painted after every "other" dot, or a same-sized cheek/forehead point
  // sitting right next to an eye landmark can paint over part of it and
  // make the eye ring look broken. Named features go last so they're
  // always fully on top.
  const otherRadius = Math.max(canvas.width / 500, 2);
  const namedRadius = Math.max(canvas.width / 220, 4);

  const drawDot = (x: number, y: number, radius: number, color: string) => {
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
    // A dark outline, not just a bright fill — the actual fix for "barely
    // visible": a fill color alone can still wash out against skin tones
    // close to it (a yellow eye dot on pale skin, a green nose dot in warm
    // light). The outline is what keeps every dot readable regardless of
    // the photo underneath.
    ctx.lineWidth = Math.max(radius / 4, 0.75);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.65)';
    ctx.stroke();
  };

  for (const landmarks of result.faceLandmarks) {
    landmarks.forEach((point, index) => {
      if (colorForLandmark(index) === OTHER_MESH_COLOR) {
        drawDot(point.x * canvas.width, point.y * canvas.height, otherRadius, OTHER_MESH_COLOR);
      }
    });
    landmarks.forEach((point, index) => {
      const color = colorForLandmark(index);
      if (color !== OTHER_MESH_COLOR) {
        drawDot(point.x * canvas.width, point.y * canvas.height, namedRadius, color);
      }
    });
  }
}

// The face's own local coordinate frame — eye-midpoint as the origin,
// inter-ocular distance as the unit — used by matchReference() to express
// any landmark's position in a scale- and position-invariant way, so it can
// be measured on one photo and re-projected onto a different one.
function faceFrame(landmarks: Landmark[], width: number, height: number) {
  const leftEye = landmarks[33];
  const rightEye = landmarks[263];
  const cx = ((leftEye?.x ?? 0) + (rightEye?.x ?? 0)) * 0.5 * width;
  const cy = ((leftEye?.y ?? 0) + (rightEye?.y ?? 0)) * 0.5 * height;
  const interOcular = Math.hypot(
    ((rightEye?.x ?? 0) - (leftEye?.x ?? 0)) * width,
    ((rightEye?.y ?? 0) - (leftEye?.y ?? 0)) * height,
  );
  return { cx, cy, interOcular };
}

/**
 * The largest dimension any uploaded photo keeps, in pixels.
 *
 * THIS IS A RENDERING FIX, NOT AN OPTIMIZATION — a real bug, reported from
 * this screen and pinned down by one decisive detail: the canvases were
 * blank on screen, but printing the same page to PDF showed every image
 * correctly. That split is the whole diagnosis. The canvas held correct
 * pixels the entire time (its own pixel-diff check passed, `getImageData`
 * returned real colours, and print — a software rendering path — came out
 * right); only on-screen compositing failed, silently, with nothing in the
 * console.
 *
 * Cause: most GPUs cap a texture at 4096px per side, and the browser
 * composites an on-screen canvas as a GPU texture. Phone photos routinely
 * exceed that on their long edge — the photo that exposed this was
 * 2835x4289 — so the canvas never painted. It does not reproduce in a
 * software-rendered browser (no texture cap), which is why it looked
 * unreproducible for a long time.
 *
 * 2048 keeps generous headroom under that cap while staying well above
 * what this screen needs: MediaPipe downsamples to a couple of hundred
 * pixels internally before detecting, so landmark accuracy is unaffected,
 * and 2048px is already more detail than the displayed canvas can show.
 */
const MAX_IMAGE_DIMENSION = 2048;

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const longestSide = Math.max(img.naturalWidth, img.naturalHeight);
      if (longestSide <= MAX_IMAGE_DIMENSION) {
        resolve(img);
        return;
      }
      // Downscale through an offscreen canvas, then hand back a real
      // <img> again rather than the canvas itself — every caller reads
      // `naturalWidth`/`naturalHeight` and passes this to `drawImage` /
      // `PIXI.Texture.from`, so keeping the same type means nothing
      // downstream has to know this happened.
      const scale = MAX_IMAGE_DIMENSION / longestSide;
      const offscreen = document.createElement('canvas');
      offscreen.width = Math.round(img.naturalWidth * scale);
      offscreen.height = Math.round(img.naturalHeight * scale);
      const offscreenCtx = offscreen.getContext('2d');
      if (!offscreenCtx) {
        reject(new Error('Could not get a 2D context to downscale the photo'));
        return;
      }
      offscreenCtx.drawImage(img, 0, 0, offscreen.width, offscreen.height);
      offscreen.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Could not downscale the photo'));
          return;
        }
        const scaled = new Image();
        scaled.onload = () => resolve(scaled);
        scaled.onerror = reject;
        scaled.src = URL.createObjectURL(blob);
      }, 'image/png');
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

function formatReport(result: FaceLandmarkerResult): string {
  const lines = [`Faces detected: ${result.faceLandmarks.length}`];

  result.faceLandmarks.forEach((landmarks, i) => {
    lines.push(`\nFace ${i + 1}: ${landmarks.length} landmarks`);
    for (const [name, idx] of Object.entries(NAMED_LANDMARKS)) {
      const p = landmarks[idx];
      if (!p) continue;
      lines.push(
        `  ${name} (#${idx}): x=${p.x.toFixed(3)} y=${p.y.toFixed(3)} z=${p.z.toFixed(3)}`,
      );
    }
  });

  const firstFaceShapes = result.faceBlendshapes?.[0]?.categories;
  if (firstFaceShapes) {
    lines.push('\nTop blendshapes, face 1 (a measurement of this photo, not a driver):');
    const shapes = [...firstFaceShapes].sort((a, b) => b.score - a.score).slice(0, 8);
    for (const s of shapes) {
      lines.push(`  ${s.categoryName}: ${s.score.toFixed(3)}`);
    }
  }

  return lines.join('\n');
}
